<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { real_id } from 'src/helpers/interface_helpers';
	import { getDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	//Design system components
	import { Input, Button, Text, Alert } from '@Shelly/shelly-design-system';
	//Components
	import CustomExpresionExamples from '../../AddonSettingsG2/AnalogInputSettings/CustomExpresionExamples.svelte';

	//Types
	import type { Device } from 'src/types/Device';
	import type { CustomExpresion, CustomExpresionTypes } from 'src/types/Peripherals';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';

	export let device: Device, channel, component, type;

	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;
	const replaceExp = new RegExp(/x{1,}/, 'gi');
	let statusType = type?.substring(1);
	const id = real_id(device.id);

	let showExamples = false;
	let formula: number = NaN;
	let worker: Worker;
	let value;
	let loading;

	let setting: CustomExpresion = { expr: null, unit: null }; //preventing setting from breaking error

	const getSettings = () => {
		if (typeof component === 'undefined') {
			component = `input:${channel}`;
		}
		if (typeof type === 'undefined') {
			type = 'xpercent';
		}
		statusType = type.substring(1);
		setting = { ...setting, ...settings[component][type] }; //preventing setting from breaking error
		value = readDeviceStatus(id)?.[component]?.[type] || 0;
		if (setting?.expr == null) setting.expr = '';
		if (setting?.unit == null) setting.unit = '';
	};

	onDestroy(() => {
		if (worker) {
			worker.terminate();
			worker = null;
		}
	});

	const createWorker = () => {
		if (worker) return;
		const blobUrl = URL.createObjectURL(
			new Blob(
				[
					'self.onmessage=e=>{try{let {x,exp}=JSON.parse(`${e.data}`);self.postMessage(new Function("x","exp",`return ${exp}`)(x,exp))}catch(e){self.postMessage(NaN)}}',
				],
				{ type: 'text/javascript' },
			),
		);
		worker = new Worker(blobUrl);
		URL.revokeObjectURL(blobUrl);
	};

	const execFormula = async (exp: string, value = 1) => {
		if (!worker) createWorker();
		worker.onmessage = (e: MessageEvent) => {
			const res = e.data,
				value = Number(isNaN(res) ? NaN : res);
			formula = value == Infinity ? 0 : value;
		};
		worker.postMessage(JSON.stringify({ x: value, exp }));
	};

	const setCustomPercentExpression = async () => {
		loading = true;
		const params = {
			id: channel,
			config: {
				[type]: { expr: setting.expr, unit: setting.unit },
			},
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${component.split(':')[0]}.SetConfig`,
			params,
		);
		const response = onSettingsResponse(res, device);
		if (response) {
			settings[component][type] = setting;
			devices_settings.update_devices_settings(id, settings);
		} else {
		}
		loading = false;
	};

	onMount(() => {
		getSettings();
	});

	$: if (setting?.expr) execFormula(setting?.expr, value);
	$: (getSettings(), channel); //change settings based on channel
</script>

<Alert
	class="mb-m"
	type="info"
	heading={$format('title_input_counter_custom_expression_description', {
		default:
			'Write in custom unit and math expression to transform your output. Both expression and unit need to be set!',
	})}
>
	<Button slot="bottom" size="m" variant="custom" on:click={() => (showExamples = true)}
		>{$format('title_input_counter_custom_expression_example_formulas', {
			default: 'Example formulas',
		})}</Button
	>
</Alert>

{#if setting?.expr && setting?.unit && !isNaN(formula)}
	<Alert
		class="mb-m"
		type="success"
		heading={$format('title_input_counter_custom_expression_preview', {
			default: 'Custom expression preview:',
		})}
		message={$format('title_input_counter_custom_expression_formula_preview', {
			values: {
				type: statusType,
				value,
				to: `${setting.expr.replace(replaceExp, 'x')} = ${formula} ${setting.unit.trim()}`,
			},
			default: 'If x {type} = {value} your expression will evaluate to {to}',
		})}
	/>
{:else}
	<Alert
		class="mb-m"
		type={setting.expr == '' || setting.unit == '' ? 'info' : 'warning'}
		heading={$format('title_input_counter_custom_expression_preview', {
			default: 'Custom expression preview',
		})}
		message={setting.expr == '' || setting.unit == ''
			? $format('title_input_counter_custom_expression_expr_empty', {
					default: 'Please fill out the Custom expression fields.',
				})
			: $format('title_input_counter_custom_expression_expr_error', {
					default: 'Incomplete or invalid formula.',
				})}
	/>
{/if}

<Text styleType="body-small" class="mb-s"
	>{$format('title_input_counter_custom_expression_formula', {
		default: 'Custom expression',
	})}</Text
>

<Input
	size="m"
	class="mb-xs"
	icon="fas fa-calculator"
	maxlength="100"
	bind:value={setting.expr}
	label="E.g x + 1"
	id="input-l2mfkpn"
	aria-labelledby="input-l2mfkpn"
/>
<Text styleType="body-small" class="mb-m"
	>{$format('title_input_counter_custom_expression_accepted_value', {
		default: 'The current value is available as x',
	})}</Text
>

<Text styleType="body-small" class="mb-s"
	>{$format('title_input_counter_custom_expression_unit', {
		default: 'Custom expression unit',
	})}</Text
>
<Input
	size="m"
	class="mb-s"
	icon="fas fa-pen"
	maxlength="20"
	bind:value={setting.unit}
	label={$format('input_custom_expresion_placeholder', {
		default: 'E.g kW or m3',
	})}
	id="input-wsmr4rq"
	aria-labelledby="input-wsmr4rq"
/>

{#if showExamples}
	<CustomExpresionExamples
		bind:showModal={showExamples}
		on:insert={(e) => {
			setting.expr = e.detail.expr;
			setting.unit = e.detail.unit;
		}}
	/>
{/if}

<Button class="mt-s mb-s" size="m" width="full" {loading} on:click={setCustomPercentExpression}
	>{$format('button_save', { default: 'Save' })}</Button
>
